// Session PJ2: the project's site survey visits, on the Details tab. The site survey first, then one row per lift (or per name), each
// with its status, type, gaps and the visits that worked on it. The one primary action is "Book a survey visit"; every other action
// sits in a menu. A row opens its survey panel under the list (no page of its own).
//
// A status is DERIVED by the server (not started, in progress, submitted, reviewed, superseded) and shown here as it comes.
// Nothing here is a price, and nothing here is a customer's contact.
import { useCallback, useEffect, useState } from "react";
import { api, type ProjectSurveyBookResult, type ProjectSurveySummaryView, type ProjectSurveysView } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { Button } from "../../../components/Button";
import { Loader } from "../../../components/Loader";
import { OverflowMenu, overflowDangerItemCls, overflowItemCls } from "../../../components/OverflowMenu";
import { StatusPill } from "../../../components/StatusPill";
import { formatDate } from "../../../lib/format";
import { AddLiftModal } from "./AddLiftModal";
import { BookVisitModal } from "./BookVisitModal";
import { PurchaseOrderFromSurveyModal } from "./PurchaseOrderFromSurveyModal";
import { ReassignVisitModal } from "./ReassignVisitModal";
import { SupplierDraftModal } from "./SupplierDraftModal";
import { SurveyDeleteModal } from "./SurveyDeleteModal";
import { SurveyPanel } from "./SurveyPanel";
import { isOpenVisit, surveyTypeWords } from "./surveyOfficeUi";

const quietBtn = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50 max-sm:min-h-11";

interface Notice {
  text: string;
  warnings: string[];
  offerSurveying: boolean;
}

export function ProjectSurveySection({ projectId, onProjectChanged }: { projectId: string; onProjectChanged?: () => void | Promise<void> }) {
  const [view, setView] = useState<ProjectSurveysView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [booking, setBooking] = useState<{ only?: string } | null>(null);
  const [addingLift, setAddingLift] = useState(false);
  const [deleting, setDeleting] = useState<{ id: string; label: string | null; kind: "site" | "lift" } | null>(null);
  const [reassigning, setReassigning] = useState<{ jobId: string; engineerName: string | null } | null>(null);
  const [supplier, setSupplier] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [movingStatus, setMovingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.projectSurveysOffice.view(projectId));
      setLoadError(null);
    } catch (e) {
      setLoadError(friendlyApiError(e, "Could not load the site survey visits."));
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loadError && !view) {
    return (
      <div className="mt-4 rounded-ele border border-ele-purple-accent p-4" data-survey-section>
        <h3 className="font-heading text-lg text-ele-strong">Site survey visits</h3>
        <p role="alert" className="mt-2 text-sm text-ele-danger">{loadError}</p>
        <button type="button" className={`${quietBtn} mt-2`} onClick={() => void load()}>Try again</button>
      </div>
    );
  }
  if (!view) {
    return (
      <div className="mt-4" data-survey-section>
        <Loader shape="panel" />
      </div>
    );
  }

  const live = view.surveys.filter((s) => s.supersededById == null);
  const hasReviewedLift = live.some((s) => s.kind === "lift" && s.status === "reviewed");
  const openVisitOf = (s: ProjectSurveySummaryView) => [...s.visits].reverse().find((v) => isOpenVisit(v.jobStatus)) ?? null;

  function booked(r: ProjectSurveyBookResult) {
    setBooking(null);
    setStatusError(null);
    setNotice({
      text: `Booked ${r.surveys.join(", ")} for ${r.engineerName} on ${formatDate(r.date)}. ${r.engineerName} has been told.`,
      warnings: [r.warnings.capacityWarning, r.warnings.leaveWarning, r.warnings.certificationWarning].filter((w): w is string => !!w),
      // Offered only while the project is still at the enquiry stage; never done for the office.
      offerSurveying: view!.project.status === "enquiry",
    });
    void load();
  }

  async function moveToSurveying() {
    setMovingStatus(true);
    setStatusError(null);
    try {
      await api.projects.update(projectId, { status: "surveying" });
      setNotice((n) => (n ? { ...n, offerSurveying: false } : n));
      await onProjectChanged?.();
      await load();
    } catch (e) {
      setStatusError(friendlyApiError(e, "Could not move the project to Surveying."));
    } finally {
      setMovingStatus(false);
    }
  }

  return (
    <div className="mt-4 rounded-ele border border-ele-purple-accent p-4" data-survey-section>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg text-ele-strong">Site survey visits</h3>
          <p className="mt-1 text-sm text-ele-body">
            Book an engineer to survey the site and each lift. What they record on the phone comes back here for you to check.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setBooking({})} size="sm">
            Book a survey visit
          </Button>
          <OverflowMenu label="Survey actions" closeOnSelect>
            <button role="menuitem" className={overflowItemCls} onClick={() => setAddingLift(true)}>
              Add a lift
            </button>
            {hasReviewedLift ? (
              <button role="menuitem" className={overflowItemCls} onClick={() => setSupplier(true)}>
                Draft to supplier
              </button>
            ) : null}
            {hasReviewedLift ? (
              <button role="menuitem" className={overflowItemCls} onClick={() => setOrdering(true)}>
                Create purchase order
              </button>
            ) : null}
          </OverflowMenu>
        </div>
      </div>

      {!view.project.hasAddress ? (
        <p className="mt-3 rounded-ele border border-ele-card-border px-3 py-2 text-sm text-[var(--ele-amber-text)]">
          This project has no site address yet. Add it under Client and site before booking a visit.
        </p>
      ) : null}

      {notice ? (
        <div role="status" className="mt-3 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong" data-booked-notice>
          <p>{notice.text}</p>
          {notice.warnings.map((w) => (
            <p key={w} className="mt-1 text-[var(--ele-amber-text)]">{w}</p>
          ))}
          {notice.offerSurveying ? (
            <div className="mt-2">
              <button type="button" className={quietBtn} onClick={() => void moveToSurveying()} disabled={movingStatus}>
                {movingStatus ? "Moving..." : "Move the project to Surveying"}
              </button>
            </div>
          ) : null}
          {statusError ? <p role="alert" className="mt-1 text-ele-danger">{statusError}</p> : null}
        </div>
      ) : null}

      {view.surveys.length === 0 ? (
        <p className="mt-4 text-sm text-ele-body">
          No survey has been set up. Book a survey visit and the site survey, and a survey for each lift on the project, are made for you.
        </p>
      ) : (
        <ul className="mt-4 space-y-2" aria-label="Surveys">
          {view.surveys.map((s) => {
            const label = s.kind === "site" ? "Site survey" : (s.label ?? "Lift");
            const done = s.status === "submitted" || s.status === "reviewed" || s.status === "superseded";
            const openVisit = openVisitOf(s);
            return (
              <li key={s.id} className={`rounded-ele border border-ele-card-border p-3 ${s.status === "superseded" ? "opacity-70" : ""}`} data-survey-row={s.id}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <button type="button" className="text-left font-heading text-base text-ele-strong underline-offset-2 hover:underline max-sm:min-h-11" onClick={() => setOpenId(s.id)} aria-label={`Open ${label}`}>
                      {label}
                    </button>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-ele-body">
                      <StatusPill domain="projectSurvey" status={s.status} />
                      {s.kind === "lift" ? <span>{surveyTypeWords(s.surveyType)}</span> : null}
                      {s.gapsCount > 0 && s.status !== "not_started" ? <span>{s.gapsCount} gap{s.gapsCount === 1 ? "" : "s"}</span> : null}
                      {s.photoCount > 0 ? <span>{s.photoCount} photo{s.photoCount === 1 ? "" : "s"}</span> : null}
                      {s.addedOnSite ? <span className="text-xs text-ele-muted">Added by the engineer on site</span> : null}
                    </div>
                    {s.visits.length > 0 ? (
                      <p className="mt-1 text-sm text-ele-body">
                        {s.visits.map((v) => `${v.engineerName ?? "No engineer yet"}${v.scheduledFor ? `, ${formatDate(v.scheduledFor)}` : ""}${v.outcome === "not_surveyed" ? " (not surveyed)" : ""}`).join("; ")}
                      </p>
                    ) : null}
                  </div>
                  <OverflowMenu label={`Actions for ${label}`} closeOnSelect>
                    <button role="menuitem" className={overflowItemCls} onClick={() => setOpenId(s.id)}>
                      View
                    </button>
                    {!done ? (
                      <button role="menuitem" className={overflowItemCls} onClick={() => setBooking({ only: s.id })}>
                        Book another visit
                      </button>
                    ) : null}
                    {openVisit ? (
                      <button role="menuitem" className={overflowItemCls} onClick={() => setReassigning({ jobId: openVisit.jobId, engineerName: openVisit.engineerName })}>
                        Change the engineer
                      </button>
                    ) : null}
                    <button role="menuitem" className={overflowDangerItemCls} onClick={() => setDeleting({ id: s.id, label: s.label, kind: s.kind })}>
                      Delete
                    </button>
                  </OverflowMenu>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {openId ? (
        <SurveyPanel
          key={openId}
          surveyId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
          onRequestDelete={(s) => setDeleting(s)}
          onResurveyed={(id) => {
            void load();
            setOpenId(id);
          }}
        />
      ) : null}

      {booking ? <BookVisitModal view={view} only={booking.only} onClose={() => setBooking(null)} onBooked={booked} /> : null}
      {addingLift ? (
        <AddLiftModal
          view={view}
          onClose={() => setAddingLift(false)}
          onAdded={() => {
            setAddingLift(false);
            void load();
          }}
        />
      ) : null}
      {reassigning ? (
        <ReassignVisitModal
          jobId={reassigning.jobId}
          currentEngineerName={reassigning.engineerName}
          onClose={() => setReassigning(null)}
          onDone={(name, warnings) => {
            setReassigning(null);
            setNotice({ text: `${name} is now on the visit and has been told.`, warnings, offerSurveying: false });
            void load();
          }}
        />
      ) : null}
      {deleting ? (
        <SurveyDeleteModal
          survey={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={(leftBooked) => {
            if (openId === deleting.id) setOpenId(null);
            setDeleting(null);
            // QUEUE1 follow-up: the survey went but a visit could not be cancelled, so the office is told it is still booked.
            if (leftBooked) setNotice({ text: "The survey was deleted.", warnings: [leftBooked], offerSurveying: false });
            void load();
          }}
        />
      ) : null}
      {ordering ? <PurchaseOrderFromSurveyModal projectId={projectId} onClose={() => setOrdering(false)} /> : null}
      {supplier ? (
        <SupplierDraftModal
          projectId={projectId}
          onClose={() => setSupplier(false)}
          onDrafted={() => {
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
